import { BarcodeFormat, DecodeHintType } from "@zxing/library";
import { BrowserMultiFormatReader } from "@zxing/browser";
import {
  PRICE_TAG_BARCODE_FORMATS,
  createNativeBarcodeDetector,
  detectFromNative,
  normalizeDetectedValue,
} from "../barcodeDetect";
import { validateBarcode } from "../eanValidator";

export interface DetectedTagCodes {
  barcode?: string;
  internalCode?: string;
}

const ZXING_FORMATS = [
  BarcodeFormat.EAN_13,
  BarcodeFormat.EAN_8,
  BarcodeFormat.UPC_A,
  BarcodeFormat.UPC_E,
  BarcodeFormat.QR_CODE,
  BarcodeFormat.CODE_128,
];

function classifyCode(raw: string): DetectedTagCodes {
  const digits = raw.replace(/\D/g, "");
  if (digits.length >= 8 && digits.length <= 14 && validateBarcode(digits).isValid) {
    return { barcode: digits };
  }
  const fromQr = raw.match(/\d{8,14}/);
  if (fromQr && validateBarcode(fromQr[0]).isValid) {
    return { barcode: fromQr[0] };
  }
  if (digits.length >= 4 && digits.length <= 14) {
    return { internalCode: digits };
  }
  return { internalCode: raw.slice(0, 32) };
}

function merge(target: DetectedTagCodes, next: DetectedTagCodes): DetectedTagCodes {
  return {
    barcode: target.barcode || next.barcode,
    internalCode: target.internalCode || next.internalCode,
  };
}

export async function detectCodesInCanvas(canvas: HTMLCanvasElement): Promise<DetectedTagCodes> {
  let found: DetectedTagCodes = {};
  const native = createNativeBarcodeDetector(PRICE_TAG_BARCODE_FORMATS);
  const nativeValues = await detectFromNative(canvas, native);
  for (const value of nativeValues) {
    found = merge(found, classifyCode(value));
  }

  if (found.barcode) return found;

  try {
    const hints = new Map();
    hints.set(DecodeHintType.POSSIBLE_FORMATS, ZXING_FORMATS);
    hints.set(DecodeHintType.TRY_HARDER, true);
    const reader = new BrowserMultiFormatReader(hints);
    const result = reader.decodeFromCanvas(canvas);
    const normalized = normalizeDetectedValue(result.getText());
    if (normalized) found = merge(found, classifyCode(normalized));
  } catch {
    // Empty crop / no code is expected.
  }

  return found;
}
